/**
 * Footer Component for DysTest
 * Author: Uttam Panda
 * Version: 4.0.0
 */

import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/Logo/logo.png";
import { useT } from "../../hooks/useT";
import { CURRENT_YEAR } from "../../constants/copyright";
import { useLanguage } from "../../contexts/LanguageContext";
import { getCookieConsentLabels } from "../../data/cookieConsentI18n";
import { OPEN_COOKIE_SETTINGS_EVENT } from "../../hooks/useCookieConsent";
import { localizedPublicPath } from '../../data/publicSeo';
import { getFooterBadgeLabels } from '../../data/footerBadgeLabels';

const COMPLIANCE_BADGES = [
  "border-blue-500/40 text-blue-400",
  "border-purple-500/40 text-purple-400",
  "border-emerald-500/40 text-emerald-400",
] as const;

const Footer: React.FC = () => {
  const { t } = useT();
  const { currentLanguage } = useLanguage();
  const cookieLabels = getCookieConsentLabels(currentLanguage);
  const badges = getFooterBadgeLabels(currentLanguage);

  const PRODUCT_LINKS = [
    { labelKey: "footer_about"   as const, to: localizedPublicPath(currentLanguage, 'about') },
    { labelKey: "footer_blog"    as const, to: "/blog" },
    { labelKey: "footer_faq"     as const, to: localizedPublicPath(currentLanguage, 'faq') },
    { labelKey: "footer_contact" as const, to: "/contact" },
  ];

  const LEGAL_LINKS = [
    { labelKey: "footer_privacy" as const, to: "/privacy-policy" },
    { labelKey: "footer_terms"   as const, to: "/terms-of-service" },
  ];

  return (
    <footer
      className="w-full bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 text-white"
      aria-label="Site footer"
    >
      {/* Top gradient accent line */}
      <div
        className="h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
        aria-hidden="true"
      />

      {/* ── Main content grid ───────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Col 1–2: Brand + tagline + tech badges */}
          <div
            className="lg:col-span-2 space-y-5"
          >
            <Link to={localizedPublicPath(currentLanguage, 'home')} aria-label="DysTest home">
              <img
                src={logo}
                alt="DysTest"
                className="h-9 w-auto brightness-0 invert opacity-85 hover:opacity-100 transition-opacity duration-200"
              />
            </Link>

            <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
              {t('footer_tagline')}
            </p>

            {/* Tech stack badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              {badges.tech.map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-1.5 bg-white/[0.06] border border-white/10 text-gray-400 px-2.5 py-1 rounded-lg text-xs font-medium"
                >
                  <span className="w-1 h-1 bg-blue-400 rounded-full" aria-hidden="true" />
                  {badge}
                </span>
              ))}
            </div>

            {/* Compliance badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              {COMPLIANCE_BADGES.map((cls, index) => (
                <span
                  key={cls}
                  className={`inline-flex items-center gap-1.5 border rounded-lg px-2.5 py-1 text-xs font-semibold ${cls}`}
                >
                  <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {badges.compliance[index]}
                </span>
              ))}
            </div>
          </div>

          {/* Col 3: Product links */}
          <div>
            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.16em] mb-5">
              {t('footer_productHeading')}
            </h3>
            <ul className="space-y-3">
              {PRODUCT_LINKS.map(({ labelKey, to }) => (
                <li key={labelKey}>
                  <Link
                    to={to}
                    className="group flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium transition-colors duration-150 focus:outline-none focus:text-white"
                  >
                    <span
                      className="block w-0 group-hover:w-3 h-px bg-blue-400 rounded-full transition-all duration-200"
                      aria-hidden="true"
                    />
                    {t(labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Legal links */}
          <div>
            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.16em] mb-5">
              {t('footer_legalHeading')}
            </h3>
            <ul className="space-y-3">
              {LEGAL_LINKS.map(({ labelKey, to }) => (
                <li key={labelKey}>
                  {/* Opens in a new tab — same-tab navigation here unmounts whatever
                      page rendered this Footer (e.g. the home page's assessment intake
                      form), silently discarding any in-progress, unsaved form state. */}
                  <Link
                    to={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium transition-colors duration-150 focus:outline-none focus:text-white"
                  >
                    <span
                      className="block w-0 group-hover:w-3 h-px bg-purple-400 rounded-full transition-all duration-200"
                      aria-hidden="true"
                    />
                    {t(labelKey)}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))}
                  className="group flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium transition-colors duration-150 focus:outline-none focus:text-white"
                >
                  <span
                    className="block w-0 group-hover:w-3 h-px bg-purple-400 rounded-full transition-all duration-200"
                    aria-hidden="true"
                  />
                  {cookieLabels.settings}
                </button>
              </li>
            </ul>

            {/* Mini mission statement */}
            <div className="mt-8 p-4 rounded-xl bg-white/[0.04] border border-white/[0.07]">
              <p className="text-gray-500 text-xs leading-relaxed">
                {t('footer_mission')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ──────────────────────────────────────── */}
      <div className="border-t border-white/[0.07]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-14 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-gray-600 text-xs">
            &copy; {CURRENT_YEAR} DysTest &mdash; {t('footer_copyright')}
          </p>

          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-xs hidden sm:block">
              {t('footer_madeWith')}
            </span>
            {/* System status */}
            <div
              className="flex items-center gap-1.5 text-emerald-500 text-xs font-semibold"
              aria-label="System status: all systems operational"
            >
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" aria-hidden="true" />
              {t('footer_allSystems')}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
